import {
    BackgroundImageRestDto,
    BorderRadiusRestDto, BorderRestDto, BoxShadowRestDto,
    ColorRestDto,
    ContentBlockInRowRestDto,
    DeviceVisibilityRestDto, EyebrowRestDto,
} from "@/api/pharma-cms-content-page/models";
import {ContentBlockStructureEnum} from "@/models/enum/ContentBlockStructureEnum";
import {BackgroundColorEnum} from "@/api/enums/background-color-enum";
import {ColumnHorizontalAlignmentEnum} from "@/api/enums/column-horizontal-alignment-enum";
import {ColumnWidthEnum} from "@/api/enums/column-width-enum";
import {PaddingBottomEnum} from "@/api/enums/padding-bottom-enum";
import {PaddingLeftEnum} from "@/api/enums/padding-left-enum";
import {PaddingRightEnum} from "@/api/enums/padding-right-enum";
import {PaddingTopEnum} from "@/api/enums/padding-top-enum";
import {BorderRadiusRestDtoModel} from "@/models/BorderRadiusRestDtoModel";
import {BackgroundImageRestDtoModel} from "@/models/api/pharma-cms-content-page/BackgroundImageRestDtoModel";
import {ColorRestDtoModel} from "@/models/api/ColorRestDtoModel";
import {EyebrowRestDtoModel} from "@/models/api/pharma-cms-content-page/EyebrowRestDtoModel";
import {BorderRestDtoModel} from "@/models/api/pharma-cms-content-page/BorderRestDtoModel";
import {BoxShadowRestDtoModel} from "@/models/api/pharma-cms-content-page/BoxShadowRestDtoModel";
import {DeviceVisibilityRestDtoModel} from "@/models/api/pharma-cms-content-page/DeviceVisibilityRestDtoModel";

export class ContentBlockInRowRestDtoModel implements ContentBlockInRowRestDto {
    cb_id?: string;
    background_color_code?: BackgroundColorEnum;
    background_color?: ColorRestDto;
    background_image?: BackgroundImageRestDto;
    border_radius: BorderRadiusRestDto;
    device_visibility?: DeviceVisibilityRestDto;
    column_horizontal_alignment_code?: ColumnHorizontalAlignmentEnum;
    column_width_code: ColumnWidthEnum;
    padding_bottom_code?: PaddingBottomEnum;
    padding_left_code?: PaddingLeftEnum;
    padding_right_code?: PaddingRightEnum;
    padding_top_code?: PaddingTopEnum;
    structure_code: ContentBlockStructureEnum;
    eyebrow?: EyebrowRestDto | null;
    border?: BorderRestDto | null;
    box_shadow?: BoxShadowRestDto | null;

    constructor(column_width_code: ColumnWidthEnum, structure_code: ContentBlockStructureEnum, cb_id?: string) {
        this.column_width_code = column_width_code;
        this.structure_code = structure_code;
        this.cb_id = cb_id;
        this.border_radius = BorderRadiusRestDtoModel.createWithDefaults();
    }

    static createWithDefaults(): ContentBlockInRowRestDtoModel {
        const model: ContentBlockInRowRestDtoModel = new this(
            ColumnWidthEnum.COL_12_12,
            ContentBlockStructureEnum.TEXT,
        );

        model.background_color_code = BackgroundColorEnum.TRANSPARENT;
        model.padding_top_code = PaddingTopEnum.P_0;
        model.padding_bottom_code = PaddingBottomEnum.P_0;
        model.padding_left_code = PaddingLeftEnum.P_0;
        model.padding_right_code = PaddingRightEnum.P_0;
        model.column_horizontal_alignment_code = ColumnHorizontalAlignmentEnum.START;

        return model;
    }

    /**
     * Produces a complete, deep copy of the source.
     *
     * The clone carries the cb_id of the source. Callers that clone into an existing column
     * (pasting metadata from the clipboard) have to overwrite it with the cb_id of that column
     * afterwards, otherwise the column would point at somebody else's content block.
     *
     * The generated DTO declares its optional fields as `T | null | undefined` while this model uses
     * `T | undefined`, so nulls coming straight from the API are normalized to undefined here.
     */
    public static cloneFrom(source: ContentBlockInRowRestDto): ContentBlockInRowRestDtoModel {
        const model: ContentBlockInRowRestDtoModel = new this(
            source.column_width_code,
            source.structure_code as ContentBlockStructureEnum,
            source.cb_id ?? undefined,
        );

        model.background_color_code = source.background_color_code ?? undefined;
        model.padding_top_code = source.padding_top_code ?? undefined;
        model.padding_bottom_code = source.padding_bottom_code ?? undefined;
        model.padding_left_code = source.padding_left_code ?? undefined;
        model.padding_right_code = source.padding_right_code ?? undefined;
        model.column_horizontal_alignment_code = source.column_horizontal_alignment_code ?? undefined;

        if (source.border_radius) {
            model.border_radius = BorderRadiusRestDtoModel.cloneFrom(source.border_radius);
        }
        if (source.background_color) {
            model.background_color = ColorRestDtoModel.cloneFrom(source.background_color);
        }

        if (source.background_image) {
            model.background_image = BackgroundImageRestDtoModel.cloneFrom(source.background_image);
        }

        if (source.eyebrow) {
            model.eyebrow = EyebrowRestDtoModel.cloneFrom(source.eyebrow);
        }

        // An empty border is dropped rather than carried over. The API returns four sides with null
        // members for a column that never had a border, and keeping that object would switch the
        // border group on with nothing in it — including a colour the group requires. Explicitly
        // null rather than left out, so saving such a column clears the empty value server-side.
        if (source.border) {
            model.border = BorderRestDtoModel.isEmpty(source.border)
                ? null
                : BorderRestDtoModel.cloneFrom(source.border);
        }

        if (source.box_shadow) {
            model.box_shadow = BoxShadowRestDtoModel.cloneFrom(source.box_shadow);
        }

        if (source.device_visibility) {
            model.device_visibility = DeviceVisibilityRestDtoModel.cloneFrom(source.device_visibility);
        }

        return model;
    }
}
